/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import React, { useState, KeyboardEvent, useEffect, useRef } from 'react';
import clsx from 'clsx';

import { useOnClickOutside } from '../../hooks/hooks';
import { useReportBuilderContext } from '../../report-builder.context';
import { useI18nConstants } from '../../use-i18n-constants';

import styles from './editable-titles.scss';

interface Props {
  title: string;
  subTitle?: string;
  viewOnly: boolean;
  isDataCard?: boolean;
}
export const EditableTitles = ({ title: orgTitle, subTitle: orgSubTitle, viewOnly, isDataCard }: Props) => {
  const {
    setWidgetTitle,
    setWidgetSubTitle,
    currentCard,
    currentCardId,
    setIsEditingTitle
  } = useReportBuilderContext();
  const { defaultEditableTitle, defaultEditableSubTitle } = useI18nConstants();
  const [titleEditableState, setTitleEditableState] = useState(false);
  const [subTitleEditableState, setSubTitleEditableState] = useState(false);
  const [title, setTitle] = useState<string>(orgTitle);
  const [subTitle, setSubTitle] = useState<string>(orgSubTitle || '');
  const [titlePendingUpdate, setTitlePendingUpdate] = useState<boolean>(false);
  const [subTitlePendingUpdate, setSubTitlePendingUpdate] = useState<boolean>(false);
  const titleTextInputRef = useRef<HTMLDivElement>(null);
  const subTitleTextInputRef = useRef<HTMLDivElement>(null);
  const isViewOnly = viewOnly && currentCardId && currentCardId === currentCard?.i;

  useEffect(() => {
    setTitle(orgTitle || '');
  }, [orgTitle]);

  useEffect(() => {
    setSubTitle(orgSubTitle || '');
  }, [orgSubTitle]);

  useEffect(() => {
    if (!isViewOnly && titlePendingUpdate && !titleEditableState && title !== orgTitle) {
      setWidgetTitle(title);
      setTitlePendingUpdate(false);
    }
  }, [titleEditableState, title, orgTitle, isViewOnly, titlePendingUpdate, setWidgetTitle]);

  useEffect(() => {
    if (!isViewOnly && subTitlePendingUpdate && !subTitleEditableState && subTitle !== orgSubTitle) {
      setWidgetSubTitle(subTitle);
      setSubTitlePendingUpdate(false);
    }
  }, [subTitleEditableState, subTitle, orgSubTitle, isViewOnly, subTitlePendingUpdate, setWidgetSubTitle]);

  useEffect(() => {
    if (titleEditableState && titleTextInputRef.current) {
      (titleTextInputRef.current.children[0] as HTMLInputElement)?.focus();
    }
  }, [titleEditableState]);

  useEffect(() => {
    if (subTitleEditableState && subTitleTextInputRef.current) {
      (subTitleTextInputRef.current.children[0] as HTMLInputElement)?.focus();
    }
  }, [subTitleEditableState]);

  useEffect(() => {
    const newIsEditingTitleState = titleEditableState || subTitleEditableState;
    if (newIsEditingTitleState) {
      setIsEditingTitle(titleEditableState || subTitleEditableState);
    } else {
      // delay turning off isEditingTitle to allow for blur event to be processed
      setTimeout(() => {
        setIsEditingTitle(false);
      }, 50);
    }
  }, [titleEditableState, subTitleEditableState, setIsEditingTitle]);

  const handleEditText = (isSubTitle: boolean) => {
    isSubTitle ? setSubTitleEditableState(true) : setTitleEditableState(true);
  };

  const handleEnter = (e: KeyboardEvent<HTMLDivElement>, isSubTitle: boolean) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      isSubTitle ? setSubTitleEditableState(false) : setTitleEditableState(false);
    }
  };

  const handleTitleChange = (txt: string) => {
    setTitle(txt);
    setTitlePendingUpdate(true);
  };
  const handleSubTitleChange = (txt: string) => {
    setSubTitle(txt);
    setSubTitlePendingUpdate(true);
  };

  const handleBlur = (isSubTitle: boolean) => {
    isSubTitle ? setSubTitleEditableState(false) : setTitleEditableState(false);
  };

  useOnClickOutside(titleTextInputRef, () => handleBlur(false));
  useOnClickOutside(subTitleTextInputRef, () => handleBlur(true));

  return (
    <div>
      {titleEditableState && !isViewOnly ? (
        <div ref={titleTextInputRef} className="input-container">
          <input
            className={clsx(styles.titleInput, isDataCard && styles.centered)}
            type="text"
            value={title}
            onChange={e => handleTitleChange(e.target.value)}
            onKeyDown={e => handleEnter(e, false)}
            onBlur={() => handleBlur(false)}
            onFocus={e => e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length)}
          />
        </div>
      ) : (
        <h3 className={clsx(!title && styles.placeholder)} onClick={() => handleEditText(false)}>
          {title || (isViewOnly ? '' : defaultEditableTitle)}
        </h3>
      )}
      {!isDataCard && (
        <>
          {subTitleEditableState && !isViewOnly ? (
            <div ref={subTitleTextInputRef} className="input-container">
              <input
                className={clsx(styles.titleInput, styles.subTitleInput)}
                type="text"
                value={subTitle}
                onChange={e => handleSubTitleChange(e.target.value)}
                onKeyDown={e => handleEnter(e, true)}
                onBlur={() => handleBlur(true)}
                onFocus={e =>
                  e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length)
                }
              />
            </div>
          ) : (
            <p className={clsx(!subTitle && styles.placeholder)} onClick={() => handleEditText(true)}>
              {subTitle || (isViewOnly ? '' : defaultEditableSubTitle)}
            </p>
          )}
        </>
      )}
    </div>
  );
};
